import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

interface TimerProps {
  initialTime: number
  runningContent: (t: number) => React.JSX.Element
  expiredContent: React.JSX.Element
}
export interface TimerRef {
  restart: () => void
  timeLeft: number
}

const Countdown = forwardRef<TimerRef, TimerProps>(
  ({ initialTime, runningContent, expiredContent }, ref) => {
    const [timeLeft, setTimeLeft] = useState(initialTime)
    const [expired, setExpired] = useState(false)
    const intervalRef = useRef<NodeJS.Timeout | null>(null)

    const clearExistingTimer = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }

    const startTimer = () => {
      setTimeLeft(initialTime)
      setExpired(false)

      intervalRef.current = setInterval(handleTick, 1000)
    }

    const handleTick = () => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearExistingTimer()
          setExpired(true)
          return 0
        }
        return prev - 1
      })
    }

    useImperativeHandle(ref, () => ({
      restart: () => {
        clearExistingTimer()
        startTimer()
      },
      timeLeft,
    }))

    useEffect(() => {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prevTimer) => {
          if (prevTimer <= 1 && intervalRef.current) {
            clearInterval(intervalRef.current)
            setExpired(true)
            return 0
          }
          return prevTimer - 1
        })
      }, 1000)

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }
      }
    }, [])

    return expired ? expiredContent : runningContent(timeLeft)
  }
)

export default Countdown
Countdown.displayName = 'Countdown'
